import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemButton,
  InputAdornment,
  TextField,
  ListItemText,
  Button,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  AccessTime,
  CalendarToday,
  Close,
  Description,
  Event,
  Person,
  Search,
} from '@mui/icons-material';
import { FormProvider } from 'react-hook-form';
import { CreateBookingDTO } from '../../types/booking';
import { Customer } from '../../types/customer';
import ResourceSection from '../../components/bookings/BookingForm/sections/ResourceSection';
import ServiceSection from '../../components/bookings/BookingForm/sections/ServiceSection';

interface ExternalEventDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedEvent: any;
  methods: any;
  selectedResourceId: number;
  customerSearch: string;
  onCustomerSearchChange: (value: string) => void;
  customers: Customer[];
  customersLoading: boolean;
  selectedCustomerForExternal: Customer | null;
  onCustomerSelect: (customer: Customer) => void;
  onCreateBooking: (data: CreateBookingDTO) => Promise<void>;
  createLoading: boolean;
}

const ExternalEventDrawer: React.FC<ExternalEventDrawerProps> = ({
  open,
  onClose,
  selectedEvent,
  methods,
  selectedResourceId,
  customerSearch,
  onCustomerSearchChange,
  customers,
  customersLoading,
  selectedCustomerForExternal,
  onCustomerSelect,
  onCreateBooking,
  createLoading,
}) => {
  const { control, watch } = methods;
  const resourceId = watch('resourceId');

  const isCacxiEvent = selectedEvent?.extendedProps?.isCacxiEvent;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <FormProvider {...methods}>
        <Box sx={{ width: 360, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={1}>
            <Typography variant="h6">
              {isCacxiEvent ? 'Event Details' : 'Create Booking from External Event'}
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center">
                  <Event sx={{ mr: 1 }} color="primary" />
                  <Box>
                    <Typography variant="subtitle2">Event Name</Typography>
                    <Typography variant="body1">{selectedEvent?.title || '—'}</Typography>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" alignItems="center">
                  <Description sx={{ mr: 1 }} color="action" />
                  <Box>
                    <Typography variant="subtitle2">Description</Typography>
                    <Typography variant="body2">
                      {selectedEvent?.extendedProps?.description || 'No description provided.'}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" alignItems="center">
                  <Person sx={{ mr: 1 }} color="action" />
                  <Box>
                    <Typography variant="subtitle2">Organizer</Typography>
                    <Typography variant="body2">
                      {selectedEvent?.extendedProps?.organizer || '—'}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" alignItems="center">
                  <CalendarToday sx={{ mr: 1 }} color="action" />
                  <Box>
                    <Typography variant="subtitle2">Start Date</Typography>
                    <Typography variant="body2">
                      {selectedEvent?.start ? new Date(selectedEvent.start).toLocaleString() : '—'}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center">
                  <AccessTime sx={{ mr: 1 }} color="action" />
                  <Box>
                    <Typography variant="subtitle2">End Date</Typography>
                    <Typography variant="body2">
                      {selectedEvent?.end ? new Date(selectedEvent.end).toLocaleString() : '—'}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Paper>

            {selectedEvent?.extendedProps?.isExternal && !isCacxiEvent && (
              <>
                <Box sx={{ mb: 2 }}>
                  <ResourceSection control={control} disabled={false} />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <ServiceSection control={control} resourceId={resourceId} disabled={false} />
                </Box>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Assign to customer
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search by name…"
                  fullWidth
                  value={customerSearch}
                  onChange={e => onCustomerSearchChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />

                <Box
                  sx={{
                    height: 200,
                    overflowY: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  {customersLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <List dense disablePadding>
                      {customers.map(c => (
                        <ListItem key={c.id} disablePadding>
                          <ListItemButton
                            onClick={() => onCustomerSelect(c)}
                            selected={selectedCustomerForExternal?.id === c.id}
                          >
                            <ListItemText
                              primary={`${c.firstName} ${c.lastName}`}
                              secondary={c.phone}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </>
            )}
          </Box>

          <Box p={2} pt={1}>
            {selectedEvent?.extendedProps?.isExternal && !isCacxiEvent ? (
              <Button
                variant="contained"
                fullWidth
                disabled={!selectedCustomerForExternal || !resourceId}
                onClick={methods.handleSubmit(async (data: CreateBookingDTO) => {
                  try {
                    const formattedData: CreateBookingDTO = {
                      ...data,
                      startTime: new Date(data.startTime).toISOString(),
                      endTime: new Date(data.endTime).toISOString(),
                      googleEventId: selectedEvent.id.replace('ext-', ''),
                      customerId: selectedCustomerForExternal?.id || 0,
                    };
                    await onCreateBooking(formattedData);
                  } catch (error) {
                    throw error;
                  }
                })}
                startIcon={createLoading ? <CircularProgress size={16} /> : null}
              >
                {createLoading ? 'Creating...' : 'Create Booking'}
              </Button>
            ) : (
              <Button variant="outlined" fullWidth onClick={onClose}>
                Close
              </Button>
            )}
          </Box>
        </Box>
      </FormProvider>
    </Drawer>
  );
};

export default ExternalEventDrawer;
